import { useRouter } from "next/router";
import { useIsMounted } from "@pancakeswap/hooks";
import { useTranslation } from "@pancakeswap/localization";
import { AtomBox } from "@pancakeswap/ui/components/AtomBox";
import throttle from "lodash/throttle";
import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { FlexGap, Text } from "../../components";
import BottomNav from "../../components/BottomNav";
import { Box } from "../../components/Box";
import Flex from "../../components/Box/Flex";
import CakePrice from "../../components/CakePrice/CakePrice";
import Footer from "../../components/Footer";
import MenuItems from "../../components/MenuItems/MenuItems";
import { SubMenuItems } from "../../components/SubMenuItems";
import { useMatchBreakpoints } from "../../contexts";
import Logo from "./components/Logo";
import { MENU_HEIGHT, MOBILE_MENU_HEIGHT, TOP_BANNER_HEIGHT, TOP_BANNER_HEIGHT_MOBILE } from "./config";
import { MenuContext } from "./context";
import { NavProps } from "./types";
import Percent from "../../../../../apps/web/src/views/Info/components/Percent";
import LogoRound from "../../components/Svg/Icons/LogoRound";
import { useAllTokenDataSWR } from "../../../../../apps/web/src/state/info/hooks";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const StyledNav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${MENU_HEIGHT}px;
  background-color: transparent;
  // background-color: ${({ theme }) => theme.nav.background};
  // border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  transform: translate3d(0, 0, 0);

  padding-left: 16px;
  padding-right: 16px;
`;

const FixedContainer = styled.div<{ showMenu: boolean; height: number }>`
  position: fixed;
  top: ${({ showMenu, height }) => (showMenu ? 0 : `-${height}px`)};
  left: 0;
  transition: top 0.2s;
  height: ${({ height }) => `${height}px`};
  width: 100%;
  z-index: 20;
`;

const TopBannerContainer = styled.div<{ height: number }>`
  height: ${({ height }) => `${height}px`};
  min-height: ${({ height }) => `${height}px`};
  max-height: ${({ height }) => `${height}px`};
  width: 100%;
`;

const BodyWrapper = styled(Box)`
  position: relative;
  display: flex;
  max-width: 100vw;
`;

const Inner = styled.div`
  flex-grow: 1;
  margin-top: 50px;
  transition: margin-top 0.2s, margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translate3d(0, 0, 0);
  max-width: 100%;
`;

const MobileView = styled.div`
  @media (max-width: 767px) {
    width: 100%;
    position: absolute;
    & {
      height: 108%;
    }
  }
`;

const Menu: React.FC<React.PropsWithChildren<NavProps>> = ({
  linkComponent = "a",
  banner,
  leftSide,
  rightSide,
  isDark,
  toggleTheme,
  currentLang,
  setLang,
  cakePriceUsd,
  links,
  subLinks,
  footerLinks,
  activeItem,
  activeSubItem,
  langs,
  buyCakeLabel,
  buyCakeLink,
  children,
}) => {
  const { isMobile } = useMatchBreakpoints();
  const isMounted = useIsMounted();
  const [showMenu, setShowMenu] = useState(true);
  const refPrevOffset = useRef(typeof window === "undefined" ? 0 : window.pageYOffset);

  const topBannerHeight = isMobile ? TOP_BANNER_HEIGHT_MOBILE : TOP_BANNER_HEIGHT;

  const totalTopMenuHeight = isMounted && banner ? MENU_HEIGHT + topBannerHeight : MENU_HEIGHT;

  useEffect(() => {
    const handleScroll = () => {
      const currentOffset = window.pageYOffset;
      const isBottomOfPage = window.document.body.clientHeight === currentOffset + window.innerHeight;
      const isTopOfPage = currentOffset === 0;
      // Always show the menu when user reach the top
      if (isTopOfPage) {
        setShowMenu(true);
      }
      // Avoid triggering anything at the bottom because of layout shift
      else if (!isBottomOfPage) {
        if (currentOffset < refPrevOffset.current || currentOffset <= totalTopMenuHeight) {
          // Has scroll up
          setShowMenu(false);
        } else {
          // Has scroll down
          // setShowMenu(false);
          setShowMenu(false);
        }
      }
      refPrevOffset.current = currentOffset;
    };
    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [totalTopMenuHeight]);

  // Find the home link if provided
  const homeLink = links.find((link) => link.label === "Home");

  const router = useRouter();

  const isSub = router.pathname === "/";

  const subLinksWithoutMobile = subLinks?.filter((subLink) => !subLink.isMobileOnly);
  const subLinksMobileOnly = subLinks?.filter((subLink) => subLink.isMobileOnly);
  const providerValue = useMemo(() => ({ linkComponent }), [linkComponent]);

  const allTokens: any = useAllTokenDataSWR();
  const formattedTokens = useMemo(() => {
    // @ts-ignore
    return Object.values(allTokens)
      .map((token: any) => token.data)
      .filter((token) => token.name !== "unknown");
  }, [allTokens]);
  const nineInchToken = formattedTokens.find((token) => token.symbol === "9INCH");

  return (
    <MenuContext.Provider value={providerValue}>
      <AtomBox
        asChild
        minHeight={{
          xs: "auto",
          md: "100vh",
        }}
      >
        <Wrapper>
          <FixedContainer showMenu={showMenu} height={totalTopMenuHeight}>
            {banner && isMounted && <TopBannerContainer height={topBannerHeight}>{banner}</TopBannerContainer>}
            <StyledNav>
              <Flex>
                <Logo href={homeLink?.href ?? "/"} />
                <AtomBox>
                  {!isMobile ? (
                    <MenuItems items={links} activeItem={activeItem} activeSubItem={activeSubItem} ml="12px" />
                  ) : (
                    <></>
                  )}
                </AtomBox>
              </Flex>
              <Flex alignItems="center" height="100%">
                {leftSide}
                <AtomBox mr="32px">
                  {isMobile && nineInchToken ? (
                    <Flex flexDirection="row">
                      <LogoRound width="24px" mr="8px" />
                      <Percent fontSize="14px" value={nineInchToken.priceUSDChange} />
                    </Flex>
                  ) : (
                    <Flex alignItems="center" height="100%">
                      {/* <CakePrice showSkeleton={false} cakePriceUsd={cakePriceUsd} buyCakeLink={buyCakeLink} />
                      <div style={{ width: "10px" }}> </div> */}
                      <LogoRound width="24px" mr="8px" />
                      {nineInchToken && <Percent fontSize="14px" value={nineInchToken.priceUSDChange} />}
                    </Flex>
                  )}
                </AtomBox>
                {/* <Box mt="4px">
                  <LangSelector
                    currentLang={currentLang}
                    langs={langs}
                    setLang={setLang}
                    buttonScale="xs"
                    color="textSubtle"
                    hideLanguage
                  />
                </Box> */}
                {rightSide}
              </Flex>
            </StyledNav>
          </FixedContainer>
          {subLinks ? (
            <Flex justifyContent="space-around" overflow="hidden">
              <SubMenuItems
                items={subLinksWithoutMobile}
                mt={`${totalTopMenuHeight + 1}px`}
                activeItem={activeSubItem}
              />

              {subLinksMobileOnly && subLinksMobileOnly?.length > 0 && (
                <SubMenuItems
                  items={subLinksMobileOnly}
                  mt={`${totalTopMenuHeight + 1}px`}
                  activeItem={activeSubItem}
                  isMobileOnly
                />
              )}
            </Flex>
          ) : isSub ? (
            <Flex justifyContent="space-around" overflow="hidden">
              <SubMenuItems
                items={[
                  {
                    label: "EXCHANGE",
                    href: "/swap",
                  },
                  {
                    label: "LIMIT",
                    href: "/limit-orders",
                  },
                ]}
                mt={`${totalTopMenuHeight + 1}px`}
              />
            </Flex>
          ) : (
            <Flex justifyContent="space-around" overflow="hidden">
              <SubMenuItems
                items={[
                  {
                    label: "LIQUIDITY",
                    href: "/liquidity",
                  },
                  {
                    label: "FARMS",
                    href: "/farms",
                  },
                  {
                    label: "POOLS",
                    href: "/pools",
                  },
                ]}
                mt={`${totalTopMenuHeight + 1}px`}
              />
            </Flex>
          )}

          <BodyWrapper mt="-50px">
            <>
              <MobileView>
                <img
                  style={{
                    position: "fixed",
                    width: "100vw",
                    height: "100vh",
                    objectFit: "cover",
                    userSelect: "none",
                  }}
                  alt=""
                  src="/images/bg2.png"
                />
              </MobileView>

              <Inner>{children}</Inner>
            </>
          </BodyWrapper>
        </Wrapper>
      </AtomBox>
      <Footer
        items={footerLinks}
        isDark={isDark}
        toggleTheme={toggleTheme}
        langs={langs}
        setLang={setLang}
        currentLang={currentLang}
        cakePriceUsd={cakePriceUsd}
        buyCakeLabel={buyCakeLabel}
        buyCakeLink={buyCakeLink}
        mb={[`${MOBILE_MENU_HEIGHT}px`, null, "0px"]}
      />
      <AtomBox display={{ xs: "block", md: "none" }}>
        <BottomNav items={links} activeItem={activeItem} activeSubItem={activeSubItem} />
      </AtomBox>
    </MenuContext.Provider>
  );
};

export default Menu;
